import { getSupabaseClient } from "@/lib/supabase/client";

/**
 * Shared singleton token-refresh promise.
 *
 * When any authenticated API client receives a 401, it calls
 * `getSharedRefreshedToken()`. All concurrent 401 callers — regardless of
 * which API module they come from — share this single promise, so the
 * dashboard's ~12 parallel reads on load issue exactly ONE
 * `refreshSession` call even when the token expires mid-burst.
 *
 * The singleton is cleared once the refresh settles (success or failure)
 * so future expiries can trigger a fresh refresh cycle.
 */
let _pendingTokenRefresh: Promise<string | null> | null = null;

// Mirrors the prefix/exact lists from AuthProvider.tsx clearAllAuthStorage.
// Kept inline to avoid a circular import (AuthProvider imports this module).
// Task #7654 — draft prefixes mirror DRAFT_SIGNOUT_KEY_PREFIXES from
// useLocalDraft: an expired session ends the account context the same way a
// sign-out does, so typed drafts must not leak to the next login.
const _AUTH_STORAGE_PREFIXES = ["ensure_user_cache", "sb-", "bac:draft:", "bac:review-draft:"] as const;
const _AUTH_STORAGE_EXACT = [
  "auth_pending_signup_meta",
  "auth_post_complete_next",
  "bac_post_auth_redirect",
  "bac:driver-invitation-signout-continuation",
] as const;

function _clearAuthStorage(): void {
  const stores: Storage[] = [];
  try { if (typeof sessionStorage !== "undefined") stores.push(sessionStorage); } catch { /* noop */ }
  try { if (typeof localStorage !== "undefined") stores.push(localStorage); } catch { /* noop */ }
  for (const store of stores) {
    try {
      const toDrop: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (!k) continue;
        if (
          (_AUTH_STORAGE_EXACT as readonly string[]).includes(k) ||
          _AUTH_STORAGE_PREFIXES.some((p) => k === p || k.startsWith(p + ":") || k.startsWith(p))
        ) {
          toDrop.push(k);
        }
      }
      for (const k of toDrop) {
        try { store.removeItem(k); } catch { /* noop */ }
      }
    } catch { /* noop */ }
  }
}

/** Module-level flag so only the first concurrent 401 fires the redirect. */
let _redirectFired = false;

/**
 * Call when the session is confirmed unrecoverable (token refresh returned
 * null, or a retry after refresh also returned 401 / session_expired). Clears
 * all auth storage and navigates to /login?reason=session_expired.
 *
 * Only the first concurrent caller fires the redirect; subsequent callers
 * return immediately so a burst of parallel 401s produces exactly one
 * navigation (Task #6917).
 */
export function forceSessionExpiredRedirect(): void {
  if (_redirectFired) return;
  _redirectFired = true;
  _clearAuthStorage();
  try {
    window.location.replace("/login?reason=session_expired");
  } catch { /* noop in non-browser envs (SSR, tests) */ }
}

/**
 * Attempt a silent token refresh, coalescing concurrent callers onto the
 * same in-flight promise.
 *
 * Returns the new access token, or `null` when the session is truly
 * expired (refresh token dead / not logged in).
 */
export function getSharedRefreshedToken(): Promise<string | null> {
  if (!_pendingTokenRefresh) {
    const sb = getSupabaseClient();
    _pendingTokenRefresh = sb
      ? sb.auth.refreshSession().then(({ data }) => data.session?.access_token ?? null)
      : Promise.resolve(null);
    // Clear the singleton once settled so a future expiry can trigger a
    // fresh refresh. `void` suppresses the unhandled-promise lint rule —
    // the result is consumed by the callers awaiting `_pendingTokenRefresh`.
    void _pendingTokenRefresh.then(
      () => { _pendingTokenRefresh = null; },
      () => { _pendingTokenRefresh = null; },
    );
  }
  return _pendingTokenRefresh;
}

/** Exposed for testing only — resets the in-flight singleton between runs. */
export function _resetPendingTokenRefresh(): void {
  _pendingTokenRefresh = null;
  _redirectFired = false;
}
